import { BrowserModule } from '@angular/platform-browser';
import { NgModule, ErrorHandler, APP_INITIALIZER, LOCALE_ID } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ToastrModule } from 'ngx-toastr';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialogModule } from '@angular/material/dialog';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSelectModule } from '@angular/material/select';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { ReactiveFormsModule } from '@angular/forms';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { JWT_OPTIONS, JwtHelperService } from '@auth0/angular-jwt';

import { AppComponent } from './app.component';
import { UserService } from './services/user.service';
import { HttpClient, HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { AppRoutingModule, routingComponents } from './app-routing.module';
import { LoginComponent } from './login/login.component';
import { HomeComponent } from './home/home.component';
import { NavMenuComponent } from './nav-menu/nav-menu.component';
import { DocumentTypeComponent } from './document-type/document-type.component';
import { DocumentTypeService } from './services/document-type.service';
import { DocumentTypeEditComponent } from './document-type-edit/document-type-edit.component';
import { DdComboComponent } from './dd-combo/dd-combo.component';
import { AttributeTypeService } from './services/attribute-type.service';
import { AttributeTypeComboComponent } from './attribute-type-combo/attribute-type-combo.component';
import { DocumentTypeAttributeListComponent } from './document-type-attribute-list/document-type-attribute-list.component';
import { ConfirmComponent } from './dialogs/confirm/confirm.component';
import { DocumentComponent } from './documents/document/document.component';
import { DocumentTypeComboComponent } from './document-type-combo/document-type-combo.component';
import { DocumentService } from './documents/services/document.service';
import { DocumentListComponent } from './documents/document-list/document-list.component';
import { DocumentHistoryComponent } from './documents/document-history/document-history.component';
import { DocumentHistoryService } from './documents/services/document-history.service';
import { FileDropModule } from './directives/file-drop/file-drop.module';
import { FileUploadService } from './directives/file-drop/file-upload.service';
import { DocumentAttributesComponent } from './documents/document-attributes/document-attributes.component';
import { DocumentViewHistoryComponent } from './documents/document-view-history/document-view-history.component';
import { DocumentHistoryDetailService } from './documents/services/document-history-detail.service';
import { LoginExternalprovidersComponent } from './sign-up/login-externalproviders/login-externalproviders.component';
import { AuthInterceptor } from './base/interceptors/auth.interceptor';
import { GlobalErrorHandler } from './base/errors/global.error.handler';
import { RolesListComponent } from './roles/roles-list/roles-list.component';
import { RolesEditComponent } from './roles/roles-edit/roles-edit.component';
import { CommentComponent } from './comment/comment.component';
import { GapiServiceService } from './services/gapi-service.service';
import { ScriptLoaderService } from './services/script-loader.service';
import { CommentService } from './services/comment.service';
import { RichTextEditorAllModule } from '@syncfusion/ej2-angular-richtexteditor';
import { NavigaionHeaderComponent } from './navigaion-header/navigaion-header.component';
import { FlexLayoutModule } from '@angular/flex-layout';
import { AppMenuComponent } from './app-menu/app-menu.component';
import { MenuService } from './services/menu.service';
import { DATETIME_FORMATS } from './modules/time-tracking/config/date-time.formats';
import { OWL_DATE_TIME_FORMATS } from 'ng-pick-datetime';
import { ColorPickerModule } from 'ngx-color-picker';
import {WebcamModule} from 'ngx-webcam';
import { environment } from '../environments/environment.prod';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { registerLocaleData } from '@angular/common';
import { ProfileComponentComponent } from './components/profile-component/profile-component.component';
import { SharedModule } from './modules/shared/shared.module';
import {TranslateLoader, TranslateModule, TranslateService} from '@ngx-translate/core';
import { HttpLoaderFactory } from './functions/http-loader-factory';
import { RolesService } from './services/roles.service';
import { MatAutocompleteModule, MatBadgeModule, MatChipsModule,
  MatDatepicker,
  MatDatepickerModule,
  MatPaginatorIntl, MatPaginatorModule, MatSlideToggleModule, MatSnackBarModule } from '@angular/material';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { SearchQueryComponent } from './documents/search-query/search-query.component';
import { DocumentFiltersComponent } from './documents/document-filters/document-filters.component';
import { DocumentReferenceComponent } from './documents/document-reference/document-reference.component';
import { PaginatorI18n } from './modules/shared/translator/PaginatorI18n';
import { DDDateAdapter } from './modules/shared/adapters/DDDateAdapter';
import { DateAttributeComponent } from './documents/attributes/date-attribute/date-attribute.component';
import { MatMomentDateModule, MAT_MOMENT_DATE_ADAPTER_OPTIONS } from '@angular/material-moment-adapter';

import {DragDropModule} from '@angular/cdk/drag-drop';
import { DisplayAttributeComponent } from './documents/display-attribute/display-attribute.component';
import { DocumentPreviewComponent } from './documents/document-preview/document-preview.component';
import { OverlayModule } from '@angular/cdk/overlay';
import { SortFilterPipe } from './documents/document-filters/sort-filter';
import { DD_DATE_FORMATS } from './formatters/moment-formats';
import localeSi from '@angular/common/locales/si';
import localeEn from '@angular/common/locales/en';
import localeDe from '@angular/common/locales/de';
import localeHr from  '@angular/common/locales/hr';
import { NgxMatDateAdapter } from '@angular-material-components/datetime-picker';

import {
  NgxMatDatetimePickerModule, 
  NgxMatNativeDateModule, 
  NgxMatTimepickerModule,
  NGX_MAT_DATE_FORMATS 
} from '@angular-material-components/datetime-picker';
import { ExportComponent } from './dialogs/export/export.component';
import { DocumentTypeReferenceOptionsComponent } from './document-type-options/document-type-reference-options/document-type-reference-options.component';
import { SubmitErrorComponent } from './dialogs/submit/submit-error/submit-error.component';
import { CachedFileService } from './directives/file-drop/cached-file-service';
import { OperationExecutingComponent } from './dialogs/operation-executing/operation-executing.component';
import { FacebookPixelModule } from './modules/facebook-pixel/facebook-pixel.module';



registerLocaleData(localeHr, 'hr');
registerLocaleData(localeEn, 'en');
registerLocaleData(localeDe, 'de');
registerLocaleData(localeSi, 'si');

export function initGapi(gapiService: GapiServiceService) {

  return () => {
    if (environment.useGdrive) {
      if (gapiService.isGoogle) {
        gapiService.initClient();
      }
    }

  };
}


@NgModule({
  declarations: [
    AppComponent,
    routingComponents,
    LoginComponent,
    HomeComponent,
    NavMenuComponent,
    DocumentTypeComponent,
    DocumentTypeEditComponent,
    DdComboComponent,
    AttributeTypeComboComponent,
    DocumentTypeAttributeListComponent,
    ConfirmComponent,
    DocumentComponent,
    DocumentTypeComboComponent,
    DocumentListComponent,
    DocumentHistoryComponent,
    DocumentAttributesComponent,
    DocumentViewHistoryComponent,
    LoginExternalprovidersComponent,
    RolesListComponent,
    RolesEditComponent,
    CommentComponent,
    NavigaionHeaderComponent,
    AppMenuComponent,
    ProfileComponentComponent,
    SearchQueryComponent,
    DocumentFiltersComponent,
    DocumentReferenceComponent,
    DateAttributeComponent,
    DisplayAttributeComponent,
    DocumentPreviewComponent,
    SortFilterPipe,
    ExportComponent,
    DocumentTypeReferenceOptionsComponent,
    SubmitErrorComponent,
    OperationExecutingComponent
  ],
  imports: [
    BrowserModule,
    HttpClientModule,
    FormsModule,
    ReactiveFormsModule,
    BrowserAnimationsModule,
    MatButtonModule,
    MatCardModule,
    MatProgressSpinnerModule,
    MatInputModule,
    MatIconModule,
    MatMenuModule,
    MatGridListModule,
    MatSlideToggleModule,
    MatCheckboxModule,
    MatToolbarModule,
    MatSnackBarModule,
    MatDatepickerModule,
    MatMomentDateModule,
    AppRoutingModule,
    MatFormFieldModule,
    MatIconModule,
    MatSidenavModule,
    MatListModule,
    MatTableModule,
    MatSelectModule,
    MatDialogModule,
    MatTooltipModule,
    MatTabsModule,
    MatProgressBarModule,
    MatAutocompleteModule,
    MatPaginatorModule,
    MatBadgeModule,
    FileDropModule,
    RichTextEditorAllModule,
    FlexLayoutModule,
    ColorPickerModule,
    WebcamModule,
    NgxMatSelectSearchModule,
    MatChipsModule,
    InfiniteScrollModule,
    DragDropModule,
    OverlayModule,
    NgxMatDatetimePickerModule,
    NgxMatTimepickerModule,
    NgxMatNativeDateModule,        
    ToastrModule.forRoot(),
    SharedModule,
    TranslateModule.forRoot({
      loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient]
      }
  }),
  FacebookPixelModule.forRoot({enabled: true, pixelId:['1155452246588596']})
  ],
  providers: [
    UserService,
    MenuService,
    CachedFileService,
    DocumentTypeService,
    AttributeTypeService,
    DocumentService,
    DocumentHistoryService,
    DocumentHistoryDetailService,
    FileUploadService,
    ScriptLoaderService,
    GapiServiceService,
    RolesService,
    CommentService,
    {
      provide: APP_INITIALIZER,
      useFactory: initGapi,
      deps: [GapiServiceService],
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true
    },
    {provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: {useUtc: true}},
    {
      provide: MatPaginatorIntl, deps: [TranslateService],
      useFactory: (translateService: TranslateService) => new PaginatorI18n(translateService).getPaginatorIntl()
    },
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandler
    },
    { provide: NGX_MAT_DATE_FORMATS, useValue: DD_DATE_FORMATS },    
    {provide: LOCALE_ID, useValue: 'si' },
    { provide: MAT_DATE_LOCALE, useValue: 'si' },
    { provide: NgxMatDateAdapter, useClass: DDDateAdapter},
    { provide: JWT_OPTIONS, useValue: JWT_OPTIONS },
    JwtHelperService
  ],
  exports: [    
  ],
  bootstrap: [AppComponent],
  entryComponents: [ConfirmComponent, ExportComponent, AppMenuComponent, DocumentPreviewComponent, DocumentTypeReferenceOptionsComponent, SubmitErrorComponent, OperationExecutingComponent]
})

export class AppModule {
}
